import { Icon } from "@thetreep/ui";
import FormattedPrice from "@components/common/FormattedPrice";
import { useTranslation } from "react-i18next";
import { Solution } from "@gen/travel/v1/search_pb";
import { formatLocalDateTime } from "@utils/formatDate";
import TransportTimeline from "@components/common/Timeline/TransportTimeline";
import { useBookingStore } from "@stores/useBookingStore";
import {
  Offer,
  Price,
  ProductBySegmentsAndPassengers,
} from "@gen/travel/v1/common_pb";
import { isTrain } from "@services/travel";
import { ExchangeOffer, ExchangeSolution } from "@gen/travel/v1/exchange_pb";

export function RecapJourney({
  offer,
  solution,
  onModifyJourney,
  type,
  price,
  outOfPocketAmount,
  ancillariesPrice,
}: {
  offer: Offer | ExchangeOffer;
  solution: Solution | ExchangeSolution;
  onModifyJourney: (type: "outward" | "inward") => void;
  type: "outward" | "inward";
  price?: Price | undefined;
  outOfPocketAmount?: Price | undefined;
  ancillariesPrice: number;
}) {
  const { t } = useTranslation();
  const { searchRequest } = useBookingStore();
  const nbPassengers = (searchRequest?.additionalPassengers?.length ?? 0) + 1;

  const groupedProducts = offer.products.reduce(
    (acc, product) => {
      const key = product.product?.code;
      if (key) {
        if (!acc[key]) {
          acc[key] = [product];
        } else {
          acc[key].push(product);
        }
      }

      return acc;
    },
    {} as Record<string, ProductBySegmentsAndPassengers[]>,
  );

  function getPrice() {
    if (price) {
      return Number(price.amount);
    } else {
      return Number(offer.initialPrice?.amount);
    }
  }

  function getOutOfPocketAmount() {
    if (outOfPocketAmount) {
      return Number(outOfPocketAmount.amount);
    } else {
      return Number(offer.outOfPocketAmount?.amount);
    }
  }

  function getAncillariesPrice() {
    return Number(ancillariesPrice * nbPassengers);
  }

  return (
    <div className="bg-white border border-grey-blue-300  rounded-lg w-full  flex flex-col">
      <div className="border-b border-grey-blue-300  p-6">
        <div className="flex items-center gap-2">
          {isTrain(solution) ? (
            <Icon src="train_4_line" size={24} color={"#3079F2"} />
          ) : (
            <Icon src="airplane_line" size={24} color={"#3079F2"} />
          )}
          <p className="text-sm">
            {formatLocalDateTime(solution?.journey?.schedule?.departure)}
          </p>
        </div>
        <div className="mt-4">
          {solution?.journey && (
            <TransportTimeline journey={solution?.journey} />
          )}
        </div>
      </div>

      <div className="border-b border-grey-blue-300 py-4 px-6 flex-1">
        <div className="flex items-center justify-between h-full">
          <div className="flex flex-col gap-2">
            {Object.entries(groupedProducts).map(([key, products]) => (
              <div
                key={key}
                className="flex items-center gap-2 text-sm text-strong "
              >
                {products.length}
                <Icon src="user_4_fill" size={18} color={"#282828"} />
                {products[0].product?.code}
              </div>
            ))}
          </div>
          <p className="text-sm text-strong font-bold">
            {" "}
            <FormattedPrice
              integerSize={16}
              decimalSize={12}
              currency="€"
              isBold
              value={getPrice()}
            />
          </p>
        </div>
      </div>
      {ancillariesPrice > 0 && (
        <div className="border-b border-grey-blue-300 py-4 px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-strong ">
              {t("recap.optionOuigo")}
            </div>
            <p className="text-sm text-strong font-bold">
              <FormattedPrice
                integerSize={16}
                decimalSize={12}
                currency="€"
                isBold
                value={getAncillariesPrice()}
              />
            </p>
          </div>
        </div>
      )}

      <div className="border-b border-grey-blue-300 py-4 px-6">
        <div className="flex items-center justify-between">
          <div className="text-[20px] text-strong font-jakarta ">Total</div>
          <p className="text-[28px] text-strong">
            <FormattedPrice
              integerSize={28}
              decimalSize={20}
              currency="€"
              isBold
              value={
                ancillariesPrice > 0
                  ? getPrice() + Number(ancillariesPrice * nbPassengers)
                  : getPrice()
              }
            />
          </p>
        </div>
      </div>
      <div className="border-b border-grey-blue-300 py-4 px-6">
        <div className="flex items-center justify-between">
          <div className="text-[20px] text-strong font-jakarta ">
            {t("recap.dueAfterCoverage")}
          </div>
          <p className="text-[28px] text-strong">
            <FormattedPrice
              integerSize={28}
              decimalSize={20}
              currency="€"
              isBold
              value={getOutOfPocketAmount()}
            />
          </p>
        </div>
      </div>

      <div
        onClick={() => onModifyJourney(type)}
        className="cursor-pointer px-6 py-3 text-base text-blue-medium font-bold flex items-center justify-center gap-2 hover:bg-light-blue-100 transition-all duration-300"
      >
        <Icon src="pencil_line" size={18} color={"#0C419A"} />
        {t(`recap.modifyTravel`)}
      </div>
    </div>
  );
}
